// Single source of truth for provider credentials used across onboarding,
// profile edit, and order-signing flows.

export const CREDENTIALS_REQUIRING_NPI = ["MD", "DO", "DPM", "ARNP", "PA"];

const LICENSE_LABELS = {
  MD: "Medical License Number",
  DO: "Medical License Number",
  DPM: "DPM License Number",
  ARNP: "ARNP License Number",
  PA: "PA License Number",
};

const LICENSE_PLACEHOLDERS = {
  MD: "Enter your Medical License Number (optional)",
  DO: "Enter your Medical License Number (optional)",
  DPM: "Enter your DPM License Number (optional)",
  ARNP: "Enter your ARNP License Number (optional)",
  PA: "Enter your PA License Number (optional)",
};

export const getLicenseLabel = (credential) => LICENSE_LABELS[credential] || "License Number";

export const getLicensePlaceholder = (credential) =>
  LICENSE_PLACEHOLDERS[credential] || "Enter your Medical License Number (optional)";

export const getCredentialsOptions = (t) => [
  { key: "", label: t("wizard.credentials.selectYourCredentials") },
  { key: "MD", label: "MD", notranslate: true },
  { key: "DO", label: "DO", notranslate: true },
  { key: "DPM", label: "DPM", notranslate: true },
  { key: "ARNP", label: "ARNP", notranslate: true },
  { key: "PA", label: "PA", notranslate: true },
  { key: "DC", label: "DC", notranslate: true },
  { key: "RN", label: "RN", notranslate: true },
  { key: "CCA", label: "CCA", notranslate: true },
  { key: "LPN", label: "LPN", notranslate: true },
  { key: "Admin", label: "Admin", notranslate: true },
  { key: "Other", label: t("wizard.credentials.other") },
];

// The "provider clinician" half of the NPI gate, factored out so the wizard's
// credentials-step gate and computeRequiresNpi share ONE definition (they must
// agree, or NPI gets required without the credentials step that sets it — or
// vice versa).
//
// Orders (DME/peptides) providers carry the "supervisor" group, not the
// encounters "doctor" group. CRITICAL: this must exclude order-MANUFACTURER
// orgs — they also report `hasOrders` (their DoctorOffice.productLines.peptides
// is true) and their admin is a supervisor, but they manage orders rather than
// place clinical orders, so they must never be pulled into the credentials/NPI
// flow. `hasOrdersManagement` (manufacturer plan) is the discriminator used
// throughout ModalWizard for exactly this purpose.
export const isOrdersProviderSupervisor = ({ hasOrders, hasOrdersManagement, isSupervisor }) =>
  !!hasOrders && !hasOrdersManagement && !!isSupervisor;

// Single source of truth for "must this user supply an NPI?". Used by the
// onboarding wizard and profile-edit so the two stay in sync.
//
// A clinician is identified the same way the backend NPI roster does
// ("group doctor OR custom.credentials in {MD,DO,DPM,ARNP,PA}"): either the
// encounters "doctor" group OR an orders "supervisor". Orders (DME/peptides)
// providers are provisioned WITHOUT the doctor group — they carry "supervisor"
// — so a doctor-group-only gate never fired for a peptides provider and the
// wizard skipped the credentials + NPI flow entirely. Scoped to `hasOrders`
// so encounters-only doctors are unaffected, and to NPI-requiring credentials
// so non-clinical supervisors / front-desk staff aren't asked for an NPI.
export const computeRequiresNpi = ({ hasOrders, isDoctor, isSupervisor, credentials }) => {
  const isProviderClinician = isDoctor || isSupervisor;
  return !!hasOrders && isProviderClinician && CREDENTIALS_REQUIRING_NPI.includes(credentials);
};

// Derived error message — auto-updates when CREDENTIALS_REQUIRING_NPI changes.
const formatOxfordList = (items) => {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

export const NPI_REQUIRED_ERROR = `NPI Number is required for ${formatOxfordList(CREDENTIALS_REQUIRING_NPI)} credentials`;

// True when this user's profile credential is one that requires an NPI
// (and therefore must pass NPPES verification before ordering).
export const isCredentialedClinician = (user) =>
  !!user?.custom?.credentials && CREDENTIALS_REQUIRING_NPI.includes(user.custom.credentials);

// True only after NPPES verification has confirmed the NPI matches this
// clinician. Set server-side after a successful "Provider - Verify NPI"
// process; cleared whenever the NPI number changes.
export const isNpiVerified = (user) => user?.custom?.npiVerified === true;

// Org-level fallback: clinical staff (RN, MA, admin) can submit orders only
// if their org has at least one verified clinician on file. Computed by the
// backend and surfaced on auth.orgInfo.
export const orgHasVerifiedClinician = (orgInfo) => orgInfo?.hasVerifiedClinician === true;
